import { useEffect, useState } from "react";
import { safeFetch } from "@/api/safeFetch";
import { useProfesional } from "@/landingPage/context/ProfesionalContext";

type WhoWeAre = {
    title: string;
    description: string;
    mission: string;
    vision: string;

    values: string[];

    extraTitle: string;
    extraDescription: string;
    extraPoints: string[];

    faqTitle: string;
    faq: {
        question: string;
        answer: string;
    }[];

    sliderImages: string[];

    team: {
        nombre: string;
        cargo: string;
        foto: string;
    }[];

    closingText: string;
};

const EMPTY_WHO: WhoWeAre = {
    title: "",
    description: "",
    mission: "",
    vision: "",

    values: [],

    extraTitle: "",
    extraDescription: "",
    extraPoints: [],

    faqTitle: "",
    faq: [],

    sliderImages: [],

    team: [],

    closingText: "",
};



export const useWhoWeAreEditor = (perfilId?: string) => {
    const { setLandingData } = useProfesional();

    const [loading, setLoading] = useState(true);

    const [alert, setAlert] = useState<{
        mensaje: string;
        type: "success" | "error" | "warning" | "info";
    } | null>(null);

    const [who, setWho] = useState<WhoWeAre>(EMPTY_WHO);

    // =========================
    // LOAD DATA
    // =========================
    useEffect(() => {
        const fetchData = async () => {
            if (!perfilId) {
                setWho(EMPTY_WHO);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                const res = await safeFetch(
                    `/api/landing/${perfilId}/who-we-are`
                );

                if (!res.ok) {
                    setWho(EMPTY_WHO);
                    setLoading(false);
                    return;
                }

                const response = await res.json();

                // 👇 IMPORTANTE: el backend devuelve el objeto directamente
                let whoWeAre = response?.data ?? EMPTY_WHO;

                // por si algún día viniera string desde backend
                if (typeof whoWeAre === "string") {
                    try {
                        whoWeAre = JSON.parse(whoWeAre);
                    } catch {
                        whoWeAre = EMPTY_WHO;
                    }
                }

                const normalized: WhoWeAre = {
                    title: whoWeAre?.title ?? "",
                    description: whoWeAre?.description ?? "",
                    mission: whoWeAre?.mission ?? "",
                    vision: whoWeAre?.vision ?? "",

                    values: Array.isArray(whoWeAre?.values)
                        ? whoWeAre.values
                        : [],

                    extraTitle: whoWeAre?.extraTitle ?? "",
                    extraDescription: whoWeAre?.extraDescription ?? "",

                    extraPoints: Array.isArray(whoWeAre?.extraPoints)
                        ? whoWeAre.extraPoints
                        : [],

                    faqTitle: whoWeAre?.faqTitle ?? "",
                    faq: Array.isArray(whoWeAre?.faq)
                        ? whoWeAre.faq
                        : [],

                    sliderImages: Array.isArray(whoWeAre?.sliderImages)
                        ? whoWeAre.sliderImages
                        : [],

                    team: Array.isArray(whoWeAre?.team)
                        ? whoWeAre.team
                        : [],

                    closingText: whoWeAre?.closingText ?? "",
                };

                setLandingData(prev => {
                    if (!prev) return prev;

                    return {
                        ...prev,
                        whoWeAre: normalized,
                    };
                });
                setWho(normalized);

            } catch (err) {
                console.error("Error loading whoWeAre:", err);

                setWho(EMPTY_WHO);

                setAlert({
                    mensaje: "⚠ Error cargando datos. Puedes editar igualmente.",
                    type: "warning",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [perfilId, setLandingData]);

    // =========================
    // UPDATE FIELD
    // =========================
    const updateField = <K extends keyof WhoWeAre>(
        field: K,
        value: WhoWeAre[K]
    ) => {
        setWho((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // =========================
    // SAVE
    // =========================
    const save = async () => {
        try {
            if (!perfilId) {
                setAlert({
                    mensaje: "❌ Perfil no encontrado",
                    type: "error",
                });
                return;
            }

            const res = await safeFetch(
                `/api/landing/${perfilId}/who-we-are`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        whoWeAre: who,
                    }),
                }
            );

            if (!res.ok) {
                const err = await res.text();
                console.error("SAVE ERROR:", err);
                throw new Error("Backend error");
            }

            setAlert({
                mensaje: "✅ Guardado correctamente",
                type: "success",
            });
        } catch (err) {
            console.error(err);

            setAlert({
                mensaje: "❌ Error al guardar",
                type: "error",
            });
        }
    };

    return {
        who,
        loading,
        alert,
        setAlert,
        updateField,
        setWho,
        save,
    };
};